import { httpDelete, httpGet, httpPost } from "@/api/connection";
import {
  connectionFailureMessage,
  isConnectionFailure,
} from "@/api/connection.types";
import type { UploaderApi } from "@/Features/Files/Uploader";
import type {
  CaseFilesApi,
  DeleteFileResponse,
  GetFilesQuery,
  GetFilesResponse,
} from "@/Features/Files/files.types";

import type {
  CompleteUploadBody,
  CompleteUploadResult,
  StartUploadBody,
  StartUploadResult,
} from "./asset-attachments.types";

// Function names mirror monolith-api src/resources/asset-attachments
// (AssetAttachmentsService.get, getOne, startUpload, completeUpload, delete).
//
// connection.js resolves HTTP failures as { success: false, error } instead of
// rejecting (and toasts once). get and delete hand that through, as FilesAPI
// does, for the shared Files table and modals. The upload calls throw instead,
// so assetFilesApi's onChange never runs after a failed complete and the
// server's message reaches the validation form's failed list.
const base = (asset_uuid: string) => `/assets/${asset_uuid}/attachments`;

const orThrow = <T>(res: unknown, step: string): T => {
  if (isConnectionFailure(res)) {
    throw new Error(
      `Attachment ${step} failed: ${connectionFailureMessage(res)}`
    );
  }
  return res as T;
};

/**
 * GET /:attachment_uuid for `{ uuid }` (the flyout refetch), else the list.
 * The one-row GET 404s once the row is deleted, which the flyout refetches
 * right after a delete, so it doesn't toast; the delete closes the flyout, so
 * the failed result is never shown.
 * Only the params the list route takes are sent: the Case Files table also
 * sends recursive, hidden and the like, which its strict schema would 400.
 */
const get = (
  asset_uuid: string,
  q: GetFilesQuery & { page_size?: number },
  options: { skipErrorToast?: boolean } = {}
): Promise<GetFilesResponse> => {
  if (q.uuid)
    return httpGet(
      `${base(asset_uuid)}/${q.uuid}`,
      {},
      {
        skipErrorToast: true,
        ...options,
      }
    );

  const {
    page,
    page_size,
    search,
    sort_by,
    sort_dir,
    filter,
    validation_uuid,
    include_pending,
  } = q;
  return httpGet(
    base(asset_uuid),
    {
      page,
      page_size,
      search,
      sort_by,
      sort_dir,
      filter,
      validation_uuid,
      include_pending,
    },
    options
  );
};

const startUpload = async (
  asset_uuid: string,
  body: StartUploadBody,
  options: { skipErrorToast?: boolean } = {}
): Promise<StartUploadResult> =>
  orThrow(
    await httpPost(`${base(asset_uuid)}/start-upload`, body, options),
    "upload start"
  );

const completeUpload = async (
  asset_uuid: string,
  attachment_uuid: string,
  body: CompleteUploadBody,
  options: { skipErrorToast?: boolean } = {}
): Promise<CompleteUploadResult> =>
  orThrow(
    await httpPost(
      `${base(asset_uuid)}/${attachment_uuid}/complete-upload`,
      body,
      options
    ),
    "upload complete"
  );

const deleteAttachment = (
  asset_uuid: string,
  attachment_uuid: string,
  options: { skipErrorToast?: boolean } = {}
): Promise<DeleteFileResponse> =>
  httpDelete(`${base(asset_uuid)}/${attachment_uuid}`, undefined, options);

/**
 * Routes for the shared Uploader. `validation_uuid` makes each upload a
 * report of that validation. `skipErrorToast` is for callers that report
 * failures themselves. The route has no PATCH to mark a row failed, so
 * `markFailed` deletes the pending row instead; the Uploader calls it only
 * before complete, so a stored file is never removed.
 */
const uploaderApi = (
  asset_uuid: string,
  {
    validation_uuid,
    skipErrorToast,
  }: { validation_uuid?: string; skipErrorToast?: boolean } = {}
): UploaderApi => ({
  startUpload: ({ uuid, parts, file }) =>
    startUpload(
      asset_uuid,
      {
        uuid,
        name: file.name,
        size: file.size as number,
        // The Uploader passes { PartNumber, ETag: "" }; the schema is strict per
        // part and rejects the extra ETag key.
        parts: parts.map(({ PartNumber }) => ({ PartNumber })),
        client_modified: file.client_modified,
        validation_uuid,
      },
      { skipErrorToast }
    ),
  completeUpload: ({ uuid, uploadId, parts, md5, sha1, sha256 }) =>
    completeUpload(
      asset_uuid,
      uuid,
      { uploadId, parts, md5, sha1, sha256 },
      { skipErrorToast }
    ),
  markFailed: ({ uuid }) =>
    deleteAttachment(asset_uuid, uuid, { skipErrorToast: true }),
});

/**
 * The asset's routes in the shape `CaseFilesProvider` takes. `onChange` runs
 * after an upload completes or a delete goes through, for what the Files
 * list refresh doesn't cover (the asset's History).
 */
const assetFilesApi = (
  asset_uuid: string,
  onChange?: () => void
): CaseFilesApi => {
  const uploader = uploaderApi(asset_uuid);
  return {
    get: (q) => get(asset_uuid, q),
    delete: async ({ uuid }) => {
      const res = await deleteAttachment(asset_uuid, uuid);
      if (!isConnectionFailure(res)) onChange?.();
      return res;
    },
    uploaderApi: {
      ...uploader,
      completeUpload: async (d) => {
        const res = await uploader.completeUpload(d);
        onChange?.();
        return res;
      },
    },
  };
};

export const AssetAttachmentsAPI = {
  get,
  startUpload,
  completeUpload,
  delete: deleteAttachment,
  uploaderApi,
  assetFilesApi,
};
